import { differenceInDays, isValid } from 'date-fns';
import { toast } from 'react-toastify';

/**
 * Converts a file type to its Portuguese display name
 * @param {('file'|'image'|'audio'|'video'|'sticker')} type - The type of file
 * @returns {string} The Portuguese display name for the file type or 'Desconhecido' if not found
 */
export const getFileTypeName = (type) => {
  const fileTypes = {
    file: 'Arquivo',
    image: 'Imagem',
    audio: 'Áudio',
    video: 'Vídeo',
    sticker: 'Figurinha'
  };

  return fileTypes[type] || 'Desconhecido';
};

/**
 * Checks if a Gupshup media URL has expired based on their 7-day retention policy
 * @param {string} source - The Gupshup media URL to check
 * @param {string|Date} time - The timestamp when the media was sent
 * @returns {boolean | undefined}
 * - `undefined`: When source is not a Gupshup URL or time is invalid
 * - `true`: When media has expired (≥ 7 days old)
 * - `false`: When media is still valid (< 7 days old)
 * @example
 * // Check if media has expired
 * const hasExpired = isSourceExpired(
 *   'https://gupshup.io/media/123',
 *   '2024-03-26T10:00:00Z'
 * ); // returns true/false/undefined
 */
export const isSourceExpired = (source, time) => {
  if (!source || !source.toLowerCase().includes('gupshup.io')) {
    return false;
  }

  const dateFromTime = new Date(time);
  if (!time || !isValid(dateFromTime)) return false;

  const EXPIRATION_DAYS = 7;

  const daysDifference = differenceInDays(new Date(), dateFromTime);

  return daysDifference >= EXPIRATION_DAYS;
};

const mimeMap = {
  pdf: "application/pdf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  mp4: "video/mp4",
  mp3: "audio/mpeg",
  ogg: "audio/ogg",
  wav: "audio/wav",
  webm: "video/webm",
  webp: "image/webp",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  csv: "text/csv",
  txt: "text/plain",
  zip: "application/zip",
  rar: "application/x-rar-compressed",
};

export const handleDownloadMedia = async (url, extension = '', incomingMimeType = '') => {
  try {
    toast.info("Preparando download...");

    const response = await fetch(url);
    if (!response.ok) throw new Error('Network response was not ok');

    const blob = await response.blob();
    // Comentarios em portugues e com linguagem corriqueira
    const cleanIncomingMimeType = incomingMimeType ? incomingMimeType.split(';')[0].trim() : '';
    const cleanBlobType = blob.type ? blob.type.split(';')[0].trim() : '';
    
    // Checa se a extensão é válida
    const isValidExtension = extension && !extension.includes('/') && extension.length <= 5;
    const safeExtension = isValidExtension ? extension.toLowerCase() : '';
    
    // Determina o mimeType mais preciso
    const mimeType = cleanIncomingMimeType || mimeMap[safeExtension] || cleanBlobType || 'application/octet-stream';
    
    const fileBlob = new Blob([blob], { type: mimeType });
    const blobUrl = window.URL.createObjectURL(fileBlob);

    const link = document.createElement('a');
    link.href = blobUrl;
    
    // Determina a melhor extensão de arquivo para salvar
    let finalExt = safeExtension;
    
    if (!finalExt && mimeType && mimeType !== 'application/octet-stream') {
       // Look-up reverso no mimeMap
       const foundExt = Object.keys(mimeMap).find(key => mimeMap[key] === mimeType);
       if (foundExt) {
         finalExt = foundExt;
       } else {
         // Fallback para mimeType, ex: "audio/ogg" -> "ogg"
         const parts = mimeType.split('/');
         if (parts.length > 1) {
           finalExt = parts[1];
           if (finalExt.includes('+')) finalExt = finalExt.split('+')[0]; // Casos como image/svg+xml -> svg
         }
       }
    }
    
    const fileName = finalExt ? `chat_file_${new Date().getTime()}.${finalExt}` : `chat_file_${new Date().getTime()}`;
    link.download = fileName;

    document.body.appendChild(link);
    link.click();
    
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
    
  } catch (err) {
    console.error("Erro no download processado:", err);
    window.open(url, '_blank');
    toast.error("O servidor bloqueou o download direto. Abrindo em nova aba.");
  }
};
